import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Gift, Clock, PackageCheck } from 'lucide-react';
import { get, post, put, del } from '@/shared/api/api_client';
import { useAuth } from '@/features/auth';
import { Navbar } from '@/widgets/Navbar';
import { RewardCard } from '@/widgets/RewardCard';
import { PointBalanceBanner } from '@/widgets/PointBalanceBanner';
import { Button, Card, Input, Modal, Badge } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';
import { DeliverRedemption } from '@/features/rewards/DeliverRedemption';
import type { Reward, Redemption } from '@/entities/reward';
import type { PointBalance } from '@/entities/ledger';

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------
interface RewardsResponse {
  rewards: Reward[];
}

interface RedemptionsResponse {
  redemptions: Redemption[];
}

interface BalanceResponse {
  balance: PointBalance;
}

// ---------------------------------------------------------------------------
// Reward form schema
// ---------------------------------------------------------------------------
const rewardSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(60),
  description: z.string().max(200).optional(),
  pointCost: z
    .number({ invalid_type_error: 'Must be a number' })
    .int()
    .min(1, 'At least 1 point')
    .max(100000),
  emoji: z.string().max(10).optional(),
  category: z.string().max(40).optional(),
});

type RewardFormValues = z.infer<typeof rewardSchema>;

// ---------------------------------------------------------------------------
// Reward form modal
// ---------------------------------------------------------------------------
function RewardFormModal({
  isOpen,
  onClose,
  initialValues,
  rewardId,
  familyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  initialValues?: Partial<RewardFormValues>;
  rewardId?: string;
  familyId: string;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const isEditing = Boolean(rewardId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<RewardFormValues>({
    resolver: zodResolver(rewardSchema),
    defaultValues: { pointCost: 100, ...initialValues },
  });

  const onSubmit = async (data: RewardFormValues) => {
    try {
      if (isEditing && rewardId) {
        await put<void>(`/families/${familyId}/rewards/${rewardId}`, data);
      } else {
        await post<void>(`/families/${familyId}/rewards`, data);
      }
      void queryClient.invalidateQueries({ queryKey: ['rewards', familyId] });
      reset();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save reward.';
      setError('root', { message });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Reward' : 'Add a Reward'}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
        <div className="flex gap-3">
          <div className="w-20 flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="emoji">Emoji</label>
            <input
              id="emoji"
              type="text"
              placeholder="🎁"
              maxLength={8}
              className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-center text-xl"
              {...register('emoji')}
            />
          </div>
          <div className="flex-1">
            <Input
              label="Reward name"
              placeholder="e.g. Extra screen time"
              error={errors.name?.message}
              {...register('name')}
            />
          </div>
        </div>

        <Input
          label="Description (optional)"
          placeholder="Any extra details..."
          error={errors.description?.message}
          {...register('description')}
        />

        <div className="flex gap-3">
          <div className="flex-1 flex flex-col gap-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="pointCost">Point cost</label>
            <input
              id="pointCost"
              type="number"
              min={1}
              max={100000}
              className={cn(
                'block w-full rounded-lg border px-3 py-2 text-sm text-slate-900',
                'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                errors.pointCost ? 'border-danger-400' : 'border-slate-300',
              )}
              {...register('pointCost', { valueAsNumber: true })}
            />
            {errors.pointCost && (
              <p className="text-xs text-danger-600">{errors.pointCost.message}</p>
            )}
          </div>
          <div className="flex-1">
            <Input
              label="Category (optional)"
              placeholder="e.g. Fun"
              error={errors.category?.message}
              {...register('category')}
            />
          </div>
        </div>

        {errors.root && (
          <p role="alert" className="text-xs text-danger-600">{errors.root.message}</p>
        )}

        <div className="flex gap-2 pt-2">
          <Button type="submit" isLoading={isSubmitting} fullWidth>
            {isEditing ? 'Save Changes' : 'Add Reward'}
          </Button>
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// RewardsPage
// ---------------------------------------------------------------------------
export default function RewardsPage(): React.ReactElement {
  const { appUser } = useAuth();
  const queryClient = useQueryClient();

  const familyId = appUser?.familyId ?? '';
  const role = appUser?.role ?? 'child';
  const childId = appUser?.uid ?? '';

  const [activeTab, setActiveTab] = useState<'catalog' | 'pending' | 'claimed' | 'my-requests'>('catalog');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editReward, setEditReward] = useState<Reward | null>(null);
  const [confirmRewardId, setConfirmRewardId] = useState<string | null>(null);

  // Queries
  const { data: rewardsData, isLoading: rewardsLoading } = useQuery<RewardsResponse>({
    queryKey: ['rewards', familyId],
    queryFn: () => get<RewardsResponse>(`/families/${familyId}/rewards`),
    enabled: Boolean(familyId),
  });

  const { data: balanceData } = useQuery<PointBalance>({
    queryKey: ['balance', familyId, childId],
    queryFn: () =>
      get<BalanceResponse>(`/families/${familyId}/balances/${childId}`).then((r) => r.balance),
    enabled: Boolean(familyId && role === 'child'),
  });

  const { data: redemptionsData, isLoading: redemptionsLoading } = useQuery<RedemptionsResponse>({
    queryKey: ['redemptions', familyId, 'pending'],
    queryFn: () => get<RedemptionsResponse>(`/families/${familyId}/redemptions/pending`),
    enabled: Boolean(familyId && role === 'parent'),
  });

  const { data: approvedRedemptionsData, isLoading: approvedRedemptionsLoading } = useQuery<RedemptionsResponse>({
    queryKey: ['redemptions', familyId, 'approved'],
    queryFn: () => get<RedemptionsResponse>(`/families/${familyId}/redemptions/approved`),
    enabled: Boolean(familyId && role === 'parent'),
  });

  const { data: myRedemptionsData, isLoading: myRedemptionsLoading } = useQuery<RedemptionsResponse>({
    queryKey: ['redemptions', familyId, 'my'],
    queryFn: () => get<RedemptionsResponse>(`/families/${familyId}/redemptions/my`),
    enabled: Boolean(familyId && role === 'child'),
  });

  // Mutations
  const redeemMutation = useMutation({
    mutationFn: (rewardId: string) =>
      post<void>(`/families/${familyId}/rewards/${rewardId}/redeem`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['redemptions', familyId] });
      void queryClient.invalidateQueries({ queryKey: ['balance', familyId] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (rewardId: string) =>
      del<void>(`/families/${familyId}/rewards/${rewardId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['rewards', familyId] }),
  });

  const approveRedemptionMutation = useMutation({
    mutationFn: (redemptionId: string) =>
      post<void>(`/families/${familyId}/redemptions/${redemptionId}/approve`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['redemptions', familyId] }),
  });

  const rejectRedemptionMutation = useMutation({
    mutationFn: ({ id, rejectionNote }: { id: string; rejectionNote: string }) =>
      post<void>(`/families/${familyId}/redemptions/${id}/reject`, { rejectionNote }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['redemptions', familyId] }),
  });

  const rewards = rewardsData?.rewards ?? [];
  const activeRewards = rewards.filter((r) => r.isActive);
  const pendingRedemptions = redemptionsData?.redemptions ?? [];
  const approvedRedemptions = approvedRedemptionsData?.redemptions ?? [];
  const myRedemptions = myRedemptionsData?.redemptions ?? [];
  const availablePoints = balanceData?.availablePoints ?? 0;

  const confirmReward = rewards.find((r) => r.id === confirmRewardId);

  const parentTabs = [
    { id: 'catalog' as const, label: 'Rewards' },
    { id: 'pending' as const, label: `Pending (${pendingRedemptions.length})` },
    { id: 'claimed' as const, label: `Claimed (${approvedRedemptions.length})` },
  ];

  const childTabs = [
    { id: 'catalog' as const, label: 'Rewards' },
    { id: 'my-requests' as const, label: 'My Requests' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Gift size={22} className="text-accent-600" aria-hidden="true" />
            <h1 className="text-xl font-bold text-slate-900">Rewards</h1>
          </div>
          {role === 'parent' && (
            <Button
              onClick={() => setShowAddModal(true)}
              leftIcon={<Plus size={16} />}
            >
              Add Reward
            </Button>
          )}
        </div>

        {/* Child balance banner (compact) */}
        {role === 'child' && (
          <PointBalanceBanner
            childId={childId}
            familyId={familyId}
            childName={appUser?.displayName ?? ''}
          />
        )}

        {/* Tabs — parent and child both get tabs */}
        <div className="flex border-b border-slate-200" role="tablist">
          {(role === 'parent' ? parentTabs : childTabs).map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex-1 py-2.5 text-sm font-medium transition-colors duration-150',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                activeTab === tab.id
                  ? 'border-b-2 border-primary-600 text-primary-700'
                  : 'border-b-2 border-transparent text-slate-500 hover:text-slate-700',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Catalog tab (parent) */}
        {role === 'parent' && activeTab === 'catalog' && (
          <div role="tabpanel" aria-label="Rewards catalog">
            {rewardsLoading ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3, 4].map((i) => <Card key={i} className="animate-pulse h-24">{null}</Card>)}
              </div>
            ) : rewards.length === 0 ? (
              <Card className="flex flex-col items-center gap-3 py-10 text-center">
                <Gift size={32} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">No rewards yet. Add one to motivate your kids!</p>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {rewards.map((reward) => (
                  <div key={reward.id} className="relative">
                    {!reward.isActive && (
                      <Badge variant="neutral" className="absolute top-3 right-3 z-10">Archived</Badge>
                    )}
                    <RewardCard
                      reward={reward}
                      availablePoints={0}
                      userRole="parent"
                      onRedeem={() => undefined}
                      onEdit={() => setEditReward(reward)}
                      onArchive={() => archiveMutation.mutate(reward.id)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pending redemptions tab (parent) */}
        {role === 'parent' && activeTab === 'pending' && (
          <div role="tabpanel" aria-label="Pending redemptions">
            {redemptionsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Card key={i} className="animate-pulse h-20">{null}</Card>)}
              </div>
            ) : pendingRedemptions.length === 0 ? (
              <Card className="flex flex-col items-center gap-2 py-10 text-center">
                <Gift size={28} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">No pending redemptions.</p>
              </Card>
            ) : (
              <div className="space-y-3" role="list">
                {pendingRedemptions.map((r) => (
                  <Card key={r.id} className="flex items-center justify-between gap-3" aria-label="listitem">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{r.rewardName}</p>
                      <p className="text-xs text-slate-500">
                        {r.childName} · {r.pointCost.toLocaleString()} pts · {formatDate(r.requestedAt)}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="primary"
                        className="bg-secondary-600 hover:bg-secondary-700 focus-visible:ring-secondary-500"
                        onClick={() => approveRedemptionMutation.mutate(r.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => rejectRedemptionMutation.mutate({ id: r.id, rejectionNote: '' })}
                      >
                        Reject
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Claimed rewards tab (parent) */}
        {role === 'parent' && activeTab === 'claimed' && (
          <div role="tabpanel" aria-label="Claimed rewards awaiting delivery">
            {approvedRedemptionsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Card key={i} className="animate-pulse h-20">{null}</Card>)}
              </div>
            ) : approvedRedemptions.length === 0 ? (
              <Card className="flex flex-col items-center gap-2 py-10 text-center">
                <PackageCheck size={28} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">No claimed rewards waiting for delivery.</p>
              </Card>
            ) : (
              <div className="space-y-3" role="list">
                {approvedRedemptions.map((r) => (
                  <div key={r.id} role="listitem">
                    <DeliverRedemption redemption={r} familyId={familyId} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Child: reward catalog tab */}
        {role === 'child' && activeTab === 'catalog' && (
          <div role="tabpanel" aria-label="Reward catalog">
            {rewardsLoading ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {[1, 2, 3, 4].map((i) => <Card key={i} className="animate-pulse h-52">{null}</Card>)}
              </div>
            ) : activeRewards.length === 0 ? (
              <Card className="flex flex-col items-center gap-3 py-10 text-center">
                <Gift size={32} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">No rewards available yet. Ask a parent to add some!</p>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {activeRewards.map((reward) => (
                  <RewardCard
                    key={reward.id}
                    reward={reward}
                    availablePoints={availablePoints}
                    userRole="child"
                    onRedeem={() => setConfirmRewardId(reward.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Child: my requests tab */}
        {role === 'child' && activeTab === 'my-requests' && (
          <div role="tabpanel" aria-label="My reward requests">
            {myRedemptionsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => <Card key={i} className="animate-pulse h-16">{null}</Card>)}
              </div>
            ) : myRedemptions.length === 0 ? (
              <Card className="flex flex-col items-center gap-2 py-10 text-center">
                <Clock size={28} className="text-slate-300" aria-hidden="true" />
                <p className="text-sm text-slate-500">You haven't requested any rewards yet.</p>
              </Card>
            ) : (
              <div className="space-y-3" role="list">
                {myRedemptions.map((r) => (
                  <Card key={r.id} className="flex items-center justify-between gap-3" aria-label="listitem">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{r.rewardName}</p>
                      <p className="text-xs text-slate-500">
                        {r.pointCost.toLocaleString()} pts · {formatDate(r.requestedAt)}
                      </p>
                      {r.rejectionNote && (
                        <p className="mt-1 text-xs text-danger-600 italic">{r.rejectionNote}</p>
                      )}
                    </div>
                    <Badge
                      variant={
                        r.status === 'delivered' ? 'success'
                        : r.status === 'approved' ? 'info'
                        : r.status === 'rejected' ? 'danger'
                        : 'warning'
                      }
                    >
                      {r.status === 'approved' ? 'Coming Soon' : r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                    </Badge>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add reward modal */}
      <RewardFormModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        familyId={familyId}
      />

      {/* Edit reward modal */}
      {editReward && (
        <RewardFormModal
          isOpen={Boolean(editReward)}
          onClose={() => setEditReward(null)}
          rewardId={editReward.id}
          initialValues={{
            name: editReward.name,
            description: editReward.description ?? undefined,
            pointCost: editReward.pointCost,
            emoji: editReward.emoji ?? undefined,
            category: editReward.category ?? undefined,
          }}
          familyId={familyId}
        />
      )}

      {/* Child: confirm redeem modal */}
      {confirmReward && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setConfirmRewardId(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`Redeem ${confirmReward.name}`}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl space-y-4 animate-[slideUp_150ms_ease-out]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="text-5xl mb-2" aria-hidden="true">{confirmReward.emoji ?? '🎁'}</div>
              <h3 className="text-lg font-bold text-slate-900">{confirmReward.name}</h3>
              <p className="text-sm text-slate-500 mt-1">
                This will cost <span className="font-semibold text-slate-700">{confirmReward.pointCost.toLocaleString()} pts</span>
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  redeemMutation.mutate(confirmRewardId!);
                  setConfirmRewardId(null);
                }}
                className="h-12 w-full rounded-xl bg-secondary-600 text-white font-semibold hover:bg-secondary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2 transition-colors duration-150"
              >
                Yes, redeem it!
              </button>
              <button
                type="button"
                onClick={() => setConfirmRewardId(null)}
                className="h-11 w-full rounded-xl text-slate-600 font-medium text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors duration-150"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
